import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { STATUS_LABEL, STATUS_STYLES, findLabel, APPLIANCE_TYPES, formatDateTime } from "@/lib/options";
import { CalendarDays, Plus, ChevronUp, ChevronDown, List as ListIcon } from "lucide-react";
import { toast } from "sonner";
import ScheduleCalendar from "@/components/schedule/ScheduleCalendar";

// Colored status dot: New=red, in-progress/pending(follow-up/incomplete)=yellow,
// Completed=green, closed-out (cancelled/declined/expired)=grey.
const STATUS_DOT = {
  new: "bg-red-500",
  in_progress: "bg-yellow-400",
  pending_approval: "bg-yellow-400",
  completed: "bg-green-500",
  cancelled: "bg-slate-400",
  declined: "bg-slate-400",
  expired: "bg-slate-300",
};
const dotClass = (s) => STATUS_DOT[s] || "bg-slate-400";
const isClosed = (s) => ["completed", "cancelled", "declined", "expired"].includes(s);

const PAGE = 50;

function groupByDay(items) {
  const groups = {};
  for (const t of items) {
    if (!t.scheduled_at) continue;
    const key = new Date(t.scheduled_at).toDateString();
    if (!groups[key]) groups[key] = [];
    groups[key].push(t);
  }
  return Object.entries(groups).sort((a, b) => new Date(a[0]) - new Date(b[0]));
}

export default function Schedule() {
  const [view, setViewState] = useState(() => localStorage.getItem("schedule_view") || "list");
  const setView = (v) => { localStorage.setItem("schedule_view", v); setViewState(v); };
  const [byId, setById] = useState({});
  const [futureSkip, setFutureSkip] = useState(0);
  const [pastSkip, setPastSkip] = useState(0);
  const [hasMoreFuture, setHasMoreFuture] = useState(false);
  const [hasMorePast, setHasMorePast] = useState(true);
  const [loading, setLoading] = useState(false);

  const merge = useCallback((items) => {
    setById((prev) => {
      const next = { ...prev };
      for (const t of items) next[t.id] = t;
      return next;
    });
  }, []);

  const loadFuture = useCallback(async (skip) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tickets/schedule/list?direction=future&skip=${skip}&limit=${PAGE}&anchor=yesterday`);
      merge(data.items);
      setFutureSkip(skip + data.items.length);
      setHasMoreFuture(data.has_more);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [merge]);

  const loadPast = useCallback(async (skip) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tickets/schedule/list?direction=past&skip=${skip}&limit=${PAGE}&anchor=yesterday`);
      merge(data.items);
      setPastSkip(skip + data.items.length);
      setHasMorePast(data.has_more);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [merge]);

  useEffect(() => { loadFuture(0); }, [loadFuture]);

  const groups = useMemo(() => groupByDay(Object.values(byId)), [byId]);
  const todayKey = new Date().toDateString();
  const yesterdayKey = new Date(Date.now() - 86400000).toDateString();

  return (
    <div className="space-y-6" data-testid="schedule-root">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Schedule</h1>
          <p className="text-sm text-slate-500 mt-1">All jobs by day — scroll for past and upcoming work.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-slate-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setView("list")}
              className={`px-3 py-2 text-sm flex items-center gap-1.5 ${view === "list" ? "bg-blue-700 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              data-testid="schedule-view-list"
            >
              <ListIcon className="w-4 h-4" /> <span className="hidden sm:inline">List</span>
            </button>
            <button
              type="button"
              onClick={() => setView("calendar")}
              className={`px-3 py-2 text-sm flex items-center gap-1.5 ${view === "calendar" ? "bg-blue-700 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              data-testid="schedule-view-calendar"
            >
              <CalendarDays className="w-4 h-4" /> <span className="hidden sm:inline">Calendar</span>
            </button>
          </div>
          <Link to="/tickets/new">
            <Button className="bg-blue-700 hover:bg-blue-800 gap-2" data-testid="schedule-new-btn">
              <Plus className="w-4 h-4" /> New Ticket
            </Button>
          </Link>
        </div>
      </div>

      {view === "calendar" ? (
        <ScheduleCalendar />
      ) : (
        <>
      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-slate-500 flex-wrap">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> New</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-yellow-400" /> In progress / follow-up</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-green-500" /> Completed</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-slate-400" /> Cancelled / declined</span>
      </div>

      {/* Load earlier (past) */}
      {hasMorePast && (
        <div className="flex justify-center">
          <Button variant="outline" className="gap-2" disabled={loading} onClick={() => loadPast(pastSkip)} data-testid="load-earlier-btn">
            <ChevronUp className="w-4 h-4" /> {loading ? "Loading…" : "Load earlier jobs"}
          </Button>
        </div>
      )}

      {groups.length === 0 && !loading && (
        <Card className="border-slate-200">
          <CardContent className="p-10 text-center text-slate-500" data-testid="empty-schedule">
            <CalendarDays className="w-8 h-8 mx-auto mb-3 text-slate-300" />
            No jobs scheduled yet. Schedule one from a ticket.
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        {groups.map(([day, list]) => {
          const isToday = day === todayKey;
          const isYesterday = day === yesterdayKey;
          return (
            <Card key={day} className={`border-slate-200 ${isToday ? "ring-1 ring-blue-200" : ""}`}>
              <CardContent className="p-5">
                <div className="font-heading font-bold text-slate-900 mb-3 flex items-center gap-2" data-testid={`day-header-${day}`}>
                  {new Date(day).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric", year: "numeric" })}
                  {isToday && <Badge className="bg-blue-600 hover:bg-blue-600 rounded-full text-[10px]">Today</Badge>}
                  {isYesterday && <Badge variant="outline" className="rounded-full text-[10px] text-slate-600">Yesterday</Badge>}
                </div>
                <div className="divide-y divide-slate-100">
                  {list.map((t) => (
                    <Link
                      key={t.id}
                      to={`/tickets/${t.id}`}
                      className={`flex items-center justify-between py-3 hover:bg-slate-50 -mx-2 px-2 rounded-md transition-colors ${isClosed(t.status) ? "opacity-60" : ""}`}
                      data-testid={`schedule-item-${t.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span
                          className={`w-3 h-3 rounded-full shrink-0 ${dotClass(t.status)}`}
                          title={STATUS_LABEL[t.status]}
                          data-testid={`status-dot-${t.id}`}
                        />
                        <div className="min-w-0">
                          <div className={`font-medium text-slate-900 truncate ${t.status === "completed" ? "line-through decoration-slate-300" : ""}`}>
                            {t.customer ? `${t.customer.first_name} ${t.customer.last_name}` : "—"}
                          </div>
                          <div className="text-xs text-slate-500 truncate">
                            {findLabel(APPLIANCE_TYPES, t.appliance_type)} · {t.customer?.phone}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <Badge variant="outline" className={`${STATUS_STYLES[t.status]} rounded-full hidden sm:inline-flex`}>{STATUS_LABEL[t.status]}</Badge>
                        <div className="text-sm text-slate-600 font-medium">{formatDateTime(t.scheduled_at)}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Load more (future) */}
      {hasMoreFuture && (
        <div className="flex justify-center">
          <Button variant="outline" className="gap-2" disabled={loading} onClick={() => loadFuture(futureSkip)} data-testid="load-more-btn">
            <ChevronDown className="w-4 h-4" /> {loading ? "Loading…" : "Load more upcoming"}
          </Button>
        </div>
      )}
        </>
      )}
    </div>
  );
}
